import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { PageContent } from 'components';
import { checkPermissions, isLoading, globalPropTypes } from 'helpers';
import { useAccessGuard } from 'hooks';
import {
  loadYcGlobalEventFormCreator,
  removeYcGlobalEventFormCreator,
  saveYcGlobalEventCreator,
  setYcGlobalEventErrorsCreator,
} from 'store/actionsCreators';
import GlobalEventForm from '../Form/GlobalEventForm';

/**
 * One record of which Young Crews attended a global event, changed.
 *
 * The entry is read by the id in the address rather than carried over from the
 * table, so the screen survives a reload and can be linked to.
 */
const EditGlobalEvent = ({
  form,
  errors,
  loading,
  userPermissions,
  loadForm,
  clearForm,
  saveEntry,
  clearErrors,
}) => {
  const { id } = useParams();

  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    checkPermissions({ roles: ['ipma_yc_global_events_manage'] }, userPermissions || []),
    '/ipma/global-events',
    'page.ipma.globalEvents.accessDenied',
  );

  useEffect(() => {
    if (!allowed) return undefined;

    loadForm(id);

    return () => clearForm();
  }, [allowed, id]);

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  return (
    <GlobalEventForm
      mode="edit"
      entry={form.participation}
      options={form}
      errors={errors}
      loading={loading}
      onSave={saveEntry}
      clearErrors={clearErrors}
    />
  );
};

EditGlobalEvent.propTypes = {
  form: PropTypes.shape({
    participation: PropTypes.shape({}),
    eventOptions: PropTypes.arrayOf(PropTypes.shape({})),
    youngCrewOptions: PropTypes.arrayOf(PropTypes.shape({})),
    yearOptions: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  errors: PropTypes.objectOf(PropTypes.string).isRequired,
  loading: PropTypes.bool.isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  loadForm: PropTypes.func.isRequired,
  clearForm: PropTypes.func.isRequired,
  saveEntry: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
};

const mapStateToProps = ({ ipma, user, loading }) => ({
  form: ipma.ycGlobalEvents.form,
  errors: ipma.ycGlobalEvents.errors,
  loading: isLoading(loading, 'GLOBAL'),
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadForm: (payload) => dispatch(loadYcGlobalEventFormCreator(payload)),
  clearForm: () => dispatch(removeYcGlobalEventFormCreator()),
  saveEntry: (payload, history) => dispatch(saveYcGlobalEventCreator(payload, history)),
  clearErrors: () => dispatch(setYcGlobalEventErrorsCreator({})),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditGlobalEvent);
